import { ExternalLink, Globe, Smartphone, Rocket, Users, Star, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import StoreBadges from "@/components/brand/StoreBadges";
import FounderAvatar from "@/components/brand/FounderAvatar";
import SocialRow from "@/components/brand/SocialRow";
import { FOUNDER, STATS, FLAGSHIPS, type Flagship } from "@/lib/brand";
import { trackProjectClick, trackChatOpen, trackCtaClick } from "@/lib/analytics";
import { SEO, breadcrumbJsonLd, absoluteUrl } from "@/lib/seo";
import { useLocalized } from "@/hooks/useLocalized";
import { useLocalizedPath } from "@/hooks/useLocalizedPath";
import { useLanguage } from "@/contexts/LanguageContext";

const Projects = () => {
    const { t } = useTranslation();
    return (
        <>
            <SEO
                title={t("meta.projects_title")}
                description={t("meta.projects_description")}
                path="/portfolio"
                jsonLd={[
                    breadcrumbJsonLd([
                        { name: t("nav.home"), path: "/" },
                        { name: t("nav.portfolio"), path: "/portfolio" },
                    ]),
                    {
                        "@context": "https://schema.org",
                        "@type": "CollectionPage",
                        name: t("meta.projects_title"),
                        description: t("meta.projects_description"),
                        url: absoluteUrl("/portfolio"),
                    },
                ]}
            />
            <div className="flex flex-col">
                <HeroSection />
                <FlagshipsSection />
                <AppsSection />
                <FounderSection />
                <CtaSection />
            </div>
        </>
    );
};

const HeroSection = () => {
    const { t } = useTranslation();
    const heroStats = [
        { icon: <Award className="w-5 h-5" />, number: STATS.projects, label: t("projects.hero_stat_projects") },
        { icon: <Users className="w-5 h-5" />, number: STATS.clients, label: t("projects.hero_stat_clients") },
        { icon: <Star className="w-5 h-5" />, number: STATS.yearsExperience, label: t("projects.hero_stat_years") },
        { icon: <Rocket className="w-5 h-5" />, number: STATS.satisfaction, label: t("projects.hero_stat_satisfaction") },
    ];

    return (
        <section className="relative min-h-[70svh] flex items-center justify-center overflow-hidden pt-28 sm:pt-32 pb-16 sm:pb-20">
            <div className="container mx-auto px-6 relative z-10">
                <div className="max-w-5xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-semibold text-white/60 mb-8">
                        <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                        {t("projects.hero_badge")}
                    </div>

                    <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black text-white mb-8 tracking-normal" style={{ lineHeight: '1.6' }}>
                        {STATS.projects} {t("projects.hero_title_projects_suffix")} <br />
                        <span className="text-muted-foreground">{STATS.clients} {t("projects.hero_title_clients_suffix")}</span>
                        <br />
                        <span className="text-primary italic">{t("projects.hero_title_global")}</span>
                    </h1>

                    <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-12 leading-relaxed font-medium">
                        {t("projects.hero_subtitle")}
                    </p>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
                        {heroStats.map((s) => (
                            <div key={s.label} className="bento-card p-6 flex flex-col items-center gap-2">
                                <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                                    {s.icon}
                                </div>
                                <p className="text-3xl md:text-4xl font-black text-white tracking-tighter">{s.number}</p>
                                <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest">{s.label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full pointer-events-none overflow-hidden">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[150px]" />
            </div>
        </section>
    );
};

const FlagshipsSection = () => {
    const { t } = useTranslation();
    return (
        <section className="py-24 border-t border-white/5">
            <div className="container mx-auto px-6">
                <div className="max-w-3xl mb-16">
                    <h2 className="text-4xl md:text-6xl font-black text-white mb-6 tracking-normal" style={{ lineHeight: '1.6' }}>
                        {t("projects.flagships_title")}
                    </h2>
                    <p className="text-lg text-muted-foreground leading-relaxed font-medium">
                        {t("projects.flagships_subtitle")}
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {FLAGSHIPS.map((p) => (
                        <FlagshipCard key={p.key} flagship={p} />
                    ))}
                </div>
            </div>
        </section>
    );
};

const FlagshipCard = ({ flagship: p }: { flagship: Flagship }) => {
    const { t } = useTranslation();
    const { lang } = useLanguage();
    const desc = useLocalized({ ar: p.descArLong, en: p.descEnLong });
    const subName = lang === "en" ? p.nameAr : p.nameEn ?? p.name;

    return (
        <a
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackProjectClick(p.name, p.url, "projects_flagships")}
            className={`group relative bento-card p-8 md:p-10 bg-gradient-to-br ${p.gradientRich} border border-white/10 hover:border-white/20 overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-primary/10`}
        >
            <div className={`absolute -top-20 -right-20 w-60 h-60 rounded-full ${p.accent} opacity-10 blur-3xl group-hover:opacity-20 transition-opacity`} />

            <div className="relative flex flex-col h-full">
                <div className="flex items-start justify-between mb-6">
                    <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10 flex items-center justify-center">
                        <Globe className="w-6 h-6 text-white" />
                    </div>
                    <ExternalLink className="w-4 h-4 text-white/50 group-hover:text-white transition-colors" />
                </div>

                <h3 className="text-3xl md:text-4xl font-black text-white mb-1 tracking-tight">{p.name}</h3>
                <p className="text-xs font-bold text-white/50 mb-5 uppercase tracking-widest">{subName}</p>

                <p className="text-sm text-muted-foreground leading-relaxed font-medium flex-1 mb-6">
                    {desc}
                </p>

                <div className="flex flex-wrap gap-1.5 mb-6">
                    {p.tags.map((tag) => (
                        <span key={tag} className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[10px] font-bold text-white/60">
                            {tag}
                        </span>
                    ))}
                </div>

                <div className="flex items-center gap-2 text-sm font-bold text-primary group-hover:text-primary/80 transition-colors pt-4 border-t border-white/5">
                    {t("projects.flagship_visit")}
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                </div>
            </div>
        </a>
    );
};

const AppsSection = () => {
    const { t } = useTranslation();
    return (
        <section className="py-24 bg-white/[0.01] border-y border-white/5">
            <div className="container mx-auto px-6">
                <div className="grid md:grid-cols-2 gap-16 items-center">
                    <div>
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-xs font-bold text-primary mb-6">
                            <Smartphone className="w-3 h-3" />
                            {t("projects.apps_badge")}
                        </div>
                        <h2 className="text-4xl md:text-5xl font-black text-white mb-6 tracking-normal" style={{ lineHeight: '1.6' }}>
                            {t("projects.apps_title")}
                        </h2>
                        <p className="text-lg text-muted-foreground leading-relaxed font-medium mb-8">
                            {t("projects.apps_subtitle")}
                        </p>
                        <StoreBadges />
                    </div>
                    <div className="relative">
                        <div className="aspect-square rounded-[3rem] bg-gradient-to-br from-primary/10 to-transparent border border-white/5 flex items-center justify-center overflow-hidden">
                            <div className="absolute inset-0 bg-grid opacity-10" />
                            <div className="relative z-10 grid grid-cols-2 gap-4 p-8">
                                {['🤖', '🍎', '📱', '🌍'].map((e, i) => (
                                    <div key={i} className="w-24 h-24 bg-white/5 backdrop-blur-3xl rounded-3xl border border-white/10 flex items-center justify-center text-5xl shadow-2xl">
                                        {e}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const FounderSection = () => {
    const { lang } = useLanguage();
    const name = lang === "en" ? FOUNDER.nameEn : FOUNDER.nameAr;
    const role = lang === "en" ? FOUNDER.titleEn : FOUNDER.titleAr;
    const bio = lang === "en" ? FOUNDER.bioEn : FOUNDER.bioAr;

    return (
        <section className="py-32">
            <div className="container mx-auto px-6">
                <div className="bento-card p-10 md:p-16 max-w-5xl mx-auto relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 blur-[100px] -mr-48 -mt-48" />
                    <div className="relative z-10 grid md:grid-cols-[auto_1fr] gap-10 items-center">
                        <FounderAvatar
                            size={192}
                            showOnlineDot
                            ringBorder
                            rounded="rounded-3xl"
                            className="shadow-2xl shadow-primary/30"
                        />
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-xs font-bold text-primary mb-4">
                                {FOUNDER.titleEn}
                            </div>
                            <h2 className="text-3xl md:text-5xl font-black text-white mb-2 tracking-tight">
                                {name}
                            </h2>
                            <p className="text-sm font-bold text-muted-foreground uppercase tracking-widest mb-6">
                                {role}
                            </p>
                            <p className="text-lg text-muted-foreground leading-relaxed font-medium mb-8">
                                {bio}
                            </p>
                            <div className="flex flex-wrap gap-3">
                                <SocialRow variant="pill" channels={['linkedin', 'youtube', 'facebook', 'telegram']} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const CtaSection = () => {
    const { t } = useTranslation();
    const localizedPath = useLocalizedPath();
    return (
        <section className="py-32 bg-white/[0.01]">
            <div className="container mx-auto px-6 text-center">
                <h2 className="text-4xl md:text-6xl font-black text-white mb-8 tracking-normal" style={{ lineHeight: '1.6' }}>
                    {t("projects.cta_title")}
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed font-medium">
                    {t("projects.cta_subtitle", { count: STATS.projects })}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                    <Button
                        onClick={() => {
                            trackChatOpen("projects_cta");
                            window.dispatchEvent(new CustomEvent('open-chat'));
                        }}
                        size="lg"
                        className="h-14 px-10 text-base rounded-full bg-gradient-to-r from-primary to-secondary text-white font-bold shadow-tech gap-2"
                    >
                        <Rocket className="w-5 h-5" />
                        {t("projects.cta_chat")}
                    </Button>
                    <Link to={localizedPath("/contact")} onClick={() => trackCtaClick("contact_us", "projects_cta")}>
                        <Button variant="outline" size="lg" className="h-14 px-10 text-base rounded-full border-white/10 hover:bg-white/5 text-white font-bold">
                            {t("projects.cta_contact")}
                        </Button>
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default Projects;
